import { useThree } from "@react-three/fiber";
import { useEffect, useMemo, useState } from "react";
import * as THREE from "three";
import type { SiteConditions } from "../../domain/foundation/siteConditions";
import { matGround } from "../../engines/materials";
import { applyBark, applySpriteTrunkMask } from "./forestBarkShader";
import {
	buildForestInstances,
	createForestCrossGeometry,
	createForestFloor,
	createTrunkGeometry,
	FOREST_SPRITE_TRUNK_TOP,
	FOREST_TEXTURES,
} from "./forestLayout";
import type { ResolvedViewerQualityTier, TimeOfDayValue } from "./viewerConfig";

const ignoreRaycast = () => undefined;

export function SceneForest({
	tier,
	groundY,
	site,
	timeOfDay,
}: {
	tier: ResolvedViewerQualityTier;
	groundY: number;
	site: SiteConditions;
	timeOfDay: TimeOfDayValue;
}) {
	const invalidate = useThree((state) => state.invalidate);
	const [textures, setTextures] = useState<THREE.Texture[]>([]);
	const geometry = useMemo(() => createForestCrossGeometry(), []);
	const floor = useMemo(
		() => createForestFloor(groundY, site),
		[groundY, site],
	);
	const instances = useMemo(
		() => buildForestInstances(tier, groundY, site),
		[tier, groundY, site],
	);
	const batches = useMemo(
		() =>
			FOREST_TEXTURES.map((_, species) =>
				instances.filter((tree) => tree.species === species),
			),
		[instances],
	);
	const trunkGeometry = useMemo(() => createTrunkGeometry(), []);
	const trunkBatches = useMemo(
		() => [
			{
				kind: "conifer" as const,
				trees: instances.filter((tree) => tree.species !== 2),
			},
			{
				kind: "birch" as const,
				trees: instances.filter((tree) => tree.species === 2),
			},
		],
		[instances],
	);
	const barkMaterials = useMemo(
		() => ({
			conifer: applyBark(
				new THREE.MeshStandardMaterial({
					roughness: 0.95,
					envMapIntensity: 0.4,
				}),
				"conifer",
			),
			birch: applyBark(
				new THREE.MeshStandardMaterial({
					roughness: 0.85,
					envMapIntensity: 0.45,
				}),
				"birch",
			),
		}),
		[],
	);
	const light =
		1.15 * (1 - THREE.MathUtils.smoothstep(timeOfDay, 0.45, 1) * 0.91);
	const spriteMaterials = useMemo(
		() =>
			textures.map((texture, species) =>
				applySpriteTrunkMask(
					new THREE.MeshBasicMaterial({
						map: texture,
						alphaTest: 0.4,
						side: THREE.DoubleSide,
						depthWrite: true,
					}),
					FOREST_SPRITE_TRUNK_TOP[species],
				),
			),
		[textures],
	);
	useEffect(() => {
		for (const material of spriteMaterials) material.color.setScalar(light);
		invalidate();
	}, [spriteMaterials, light, invalidate]);
	useEffect(
		() => () => {
			for (const material of spriteMaterials) material.dispose();
		},
		[spriteMaterials],
	);

	// Decorative downloads never suspend the sauna or block first-frame readiness.
	// One invalidation when ready; no continuous camera-facing or wind update loop.
	useEffect(() => {
		let cancelled = false;
		const loader = new THREE.TextureLoader();
		const owned: THREE.Texture[] = [];
		const pending = FOREST_TEXTURES.map(
			(url) =>
				new Promise<THREE.Texture>((resolve, reject) => {
					const texture = loader.load(url, resolve, undefined, reject);
					texture.colorSpace = THREE.SRGBColorSpace;
					texture.minFilter = THREE.LinearMipmapLinearFilter;
					texture.magFilter = THREE.LinearFilter;
					owned.push(texture);
				}),
		);
		Promise.all(pending)
			.then((loaded) => {
				if (cancelled) return;
				setTextures(loaded);
				invalidate();
			})
			.catch(() => {
				// Keep the sauna usable if a decorative texture cannot be loaded.
				owned.forEach((texture) => texture.dispose());
			});
		return () => {
			cancelled = true;
			owned.forEach((texture) => texture.dispose());
		};
	}, [invalidate]);
	useEffect(() => () => geometry.dispose(), [geometry]);
	useEffect(() => () => trunkGeometry.dispose(), [trunkGeometry]);
	useEffect(
		() => () => {
			barkMaterials.conifer.dispose();
			barkMaterials.birch.dispose();
		},
		[barkMaterials],
	);
	useEffect(() => () => floor.dispose(), [floor]);

	return (
		<group name="dressing-forest">
			<mesh
				name="dressing-forest-floor"
				geometry={floor}
				material={matGround}
				raycast={ignoreRaycast}
			/>
			{trunkBatches.map(({ kind, trees }) => (
				<instancedMesh
					key={`${kind}-${trees.length}`}
					name={`dressing-forest-trunks-${kind}`}
					args={[trunkGeometry, barkMaterials[kind], trees.length]}
					raycast={ignoreRaycast}
					ref={(mesh) => {
						if (!mesh || typeof mesh.setMatrixAt !== "function") return;
						trees.forEach((tree, index) => {
							mesh.setMatrixAt(index, tree.trunk);
						});
						mesh.instanceMatrix.needsUpdate = true;
						mesh.computeBoundingSphere();
					}}
				/>
			))}
			{spriteMaterials.map((material, species) => (
				<instancedMesh
					key={FOREST_TEXTURES[species]}
					name={`dressing-forest-trees-${species}`}
					args={[geometry, material, batches[species].length]}
					raycast={ignoreRaycast}
					ref={(mesh) => {
						if (!mesh || typeof mesh.setMatrixAt !== "function") return;
						batches[species].forEach((tree, index) => {
							mesh.setMatrixAt(index, tree.matrix);
							mesh.setColorAt(index, new THREE.Color().setScalar(tree.gain));
						});
						mesh.instanceMatrix.needsUpdate = true;
						if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
						mesh.computeBoundingSphere();
					}}
				/>
			))}
		</group>
	);
}
